/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import React from 'react';
import { css, StyleSheet } from 'aphrodite';

import { matchPath, useHistory, useLocation } from 'react-router';
import { RouterStates } from '../../../definitions/RouterStates';
import { Wrapper, Header, Table, Footer, Error, Status } from '../../../components/common/Pipeline';
import { NotaryAPI } from '../../../api/notary/types';
import { notaryAPI } from '../../../api/notary';
import { Tabs } from '../../../components/common/enterprise/Tabs/Tabs';
import { SearchFunction } from '../../../components/TypeaheadSearch/types';
import { TypeaheadSearch } from '../../../components/TypeaheadSearch/TypeaheadSearch';
import { defaultPipelineFilters } from '../../../../src/utils/filters';
import { usePermissions } from '../../../../src/hooks/usePermissions';
import { RoomsPipelineFilter } from './RoomsPipelineFilter/RoomsPipelineFilter';
import { getColumnConfigurations } from './configs';
import { PipelineSearchResult } from './RoomsPipelineSearchResult';
import { FilterParams } from '@shared/types/pipeline';
import { isEmpty } from '@utils/lodash';
import { openRoomInNewWindow } from '@utils/routing';

const SEARCH_RESULT_LIMIT = 8;

const styles = StyleSheet.create({
  tabsWrapper: {
    marginLeft: 40,
  },
  actionSearch: {
    marginRight: 8,
    width: 400,
  },
  actionWrapper: {
    display: 'flex',
    justifyContent: 'right',
    marginRight: 40,
  },
});

export namespace RoomsPipeline {
  export interface Props {
    rooms: NotaryAPI.RoomListItem[];
    isLoading: boolean;
    hasError: boolean;
    page: number;
    totalCount: number;
    fetchRooms: (filters: FilterParams) => Promise<void>;
    setPage: (page: number) => void;
  }
}

export const RoomsPipeline: React.FC<RoomsPipeline.Props> = ({
  rooms,
  isLoading,
  hasError,
  page,
  totalCount,
  fetchRooms,
  setPage,
}) => {
  const history = useHistory();
  const location = useLocation();
  const hasSuperAdminPermission = usePermissions().includes(NotaryAPI.BSRPermissions.BSRSuperAdmin);

  const activeTab = matchPath(location.pathname, { path: RouterStates.ROOMS, exact: true })
    ? 'otherRooms'
    : 'myAppointments';

  const tabsConfig = [
    {
      id: 'myAppointments',
      label: 'Assigned To Me',
      onClick: () => {
        const params = new URLSearchParams(location.search.slice(1));
        params.set('page', '0');
        history.push(`${RouterStates.ROOM_ASSIGNMENTS}?${params.toString()}`);
      },
    },
    {
      id: 'otherRooms',
      label: 'All Notarizations',
      onClick: () => {
        const params = new URLSearchParams(location.search.slice(1));
        params.set('page', '0');
        history.push(`${RouterStates.ROOMS}?${params.toString()}`);
      },
    },
  ];
  const roomSearch: SearchFunction<NotaryAPI.RoomListItem> = async (query: string) => {
    const result = await notaryAPI.rooms.search(query, SEARCH_RESULT_LIMIT);
    return {
      items: result.rooms,
      totalCount: result.totalCount,
    };
  };

  return (
    <Wrapper>
      <Header header="Notarizations" />
      <div className={css(styles.actionWrapper)} id="it-search-header">
        <div className={css(styles.actionSearch)} id="it-search">
          <TypeaheadSearch
            ResultComponent={PipelineSearchResult}
            id="pipeline-search"
            name="Pipeline"
            onResultClick={room => {
              openRoomInNewWindow(room.id);
            }}
            inputThreshold={3}
            placeholder="Search by name or loan number"
            clearOnSelect={false}
            refocusOnSelect
            searchFunction={roomSearch}
            searchResultLimit={SEARCH_RESULT_LIMIT}
          />
        </div>
        <div id="it-filter">
          <RoomsPipelineFilter />
        </div>
      </div>
      <div className={css(styles.tabsWrapper)} id="it-tabs">
        <Tabs activeTab={activeTab} tabsConfig={tabsConfig} />
      </div>
      <Table
        data={rooms}
        onRowClick={room => {
          openRoomInNewWindow(room.id);
        }}
        tableColumns={getColumnConfigurations(fetchRooms, defaultPipelineFilters(hasSuperAdminPermission))}
      />
      <Footer page={page} onPageChange={setPage} totalCount={totalCount} />
      {hasError && <Error />}
      <Status isLoading={isLoading} isEmpty={isEmpty(rooms)} emptyMessage={'No rooms here at the moment.'} />
    </Wrapper>
  );
};
